/**
 * Intrapartum EFM (electronic fetal monitoring) pattern presets.
 *
 * Each preset sets the fetal store fields to clinically-typical values for a
 * recognisable cardiotocograph (CTG) pattern. The instructor can apply a preset
 * and then fine-tune baseline / variability / contractions with the sliders.
 *
 * Pattern recognition references:
 *   • perinatology.com — Intrapartum Monitoring
 *   • ob-efm.com — Basic Pattern Recognition
 *
 * Fields (mirror store `fetal`):
 *   fhrBaseline (bpm) · variability ('absent'|'minimal'|'moderate'|'marked')
 *   accels (bool) · decel ('none'|'early'|'late'|'variable'|'prolonged')
 *   sinusoidal (bool) · contractionFreq (per 10 min) · contractionStrength (0–100)
 *   uterineTone (0–100 resting)
 */
export const FETAL_PATTERNS = {
  normal: {
    name: 'Reassuring (Cat I)',
    category: 'I',
    description:
      'Baseline 110–160, moderate variability, accelerations present, no decelerations. ' +
      'Normal acid–base status — no intervention needed.',
    fhrBaseline: 140, variability: 'moderate', accels: true, decel: 'none',
    sinusoidal: false, contractionFreq: 3, contractionStrength: 55, uterineTone: 12,
  },
  tachycardia: {
    name: 'Fetal Tachycardia',
    category: 'II',
    description:
      'Baseline > 160 bpm (here ~180). Causes: maternal fever/chorioamnionitis, ' +
      'medications, fetal hypoxia, prematurity. Often with reduced variability.',
    fhrBaseline: 180, variability: 'minimal', accels: false, decel: 'none',
    sinusoidal: false, contractionFreq: 3, contractionStrength: 55, uterineTone: 12,
  },
  bradycardia: {
    name: 'Fetal Bradycardia',
    category: 'II–III',
    description:
      'Baseline < 110 bpm (here ~95). Causes: hypoxia, cord compression, maternal ' +
      'hypotension, rapid descent, uterine hyperstimulation. Sustained brady is ominous.',
    fhrBaseline: 95, variability: 'minimal', accels: false, decel: 'none',
    sinusoidal: false, contractionFreq: 4, contractionStrength: 60, uterineTone: 14,
  },
  earlyDecels: {
    name: 'Early Decelerations',
    category: 'I',
    description:
      'Gradual dips that MIRROR the contraction — nadir coincides with the peak. ' +
      'Vagal response to fetal head compression. Benign, no intervention.',
    fhrBaseline: 140, variability: 'moderate', accels: true, decel: 'early',
    sinusoidal: false, contractionFreq: 4, contractionStrength: 60, uterineTone: 12,
  },
  lateDecels: {
    name: 'Late Decelerations',
    category: 'II–III',
    description:
      'Gradual dips beginning AFTER the contraction starts, nadir after the peak, ' +
      'recovery after the contraction ends. Uteroplacental insufficiency — concerning, ' +
      'especially with minimal/absent variability. Reposition, O₂, fluids, stop oxytocin.',
    fhrBaseline: 145, variability: 'minimal', accels: false, decel: 'late',
    sinusoidal: false, contractionFreq: 4, contractionStrength: 65, uterineTone: 14,
  },
  variableDecels: {
    name: 'Variable Decelerations',
    category: 'II',
    description:
      'Abrupt, sharp drops (≥15 bpm, ≥15 s), variable in timing/shape — often ' +
      'V/W/U-shaped. Cord compression. Reposition; concerning if deep/prolonged or ' +
      'with loss of variability.',
    fhrBaseline: 140, variability: 'moderate', accels: true, decel: 'variable',
    sinusoidal: false, contractionFreq: 4, contractionStrength: 62, uterineTone: 12,
  },
  prolongedDecel: {
    name: 'Prolonged Deceleration',
    category: 'II–III',
    description:
      'A single deceleration ≥ 15 bpm lasting 2–10 min. Causes: cord prolapse, ' +
      'placental abruption, uterine rupture, hypotension, hyperstimulation. Acute — ' +
      'call for help, treat the cause, prepare for delivery.',
    fhrBaseline: 140, variability: 'minimal', accels: false, decel: 'prolonged',
    sinusoidal: false, contractionFreq: 3, contractionStrength: 60, uterineTone: 14,
  },
  minimalVariability: {
    name: 'Minimal / Absent Variability',
    category: 'III',
    description:
      'Flat baseline with < 5 bpm fluctuation and no accelerations. Fetal sleep, ' +
      'medications, acidosis, or CNS depression. Absent variability with recurrent ' +
      'late/variable decels is Category III.',
    fhrBaseline: 140, variability: 'absent', accels: false, decel: 'none',
    sinusoidal: false, contractionFreq: 3, contractionStrength: 55, uterineTone: 12,
  },
  sinusoidal: {
    name: 'Sinusoidal',
    category: 'III',
    description:
      'Smooth, regular sine wave (3–5 cycles/min, ~10 bpm amplitude) with no ' +
      'variability. Fetal anaemia (e.g. Rh disease, feto-maternal haemorrhage), ' +
      'severe hypoxia. Ominous — urgent evaluation.',
    fhrBaseline: 140, variability: 'absent', accels: false, decel: 'none',
    sinusoidal: true, contractionFreq: 3, contractionStrength: 55, uterineTone: 12,
  },
  quiescent: {
    name: 'No Contractions',
    category: '—',
    description:
      'Reassuring FHR with a quiescent uterus (no contractions) — e.g. antenatal ' +
      'surveillance / NST between labour, or after tocolysis.',
    fhrBaseline: 140, variability: 'moderate', accels: true, decel: 'none',
    sinusoidal: false, contractionFreq: 0, contractionStrength: 0, uterineTone: 8,
  },
};

// Order for rendering the preset buttons (roughly reassuring → ominous).
export const FETAL_PATTERN_ORDER = [
  'normal', 'quiescent', 'earlyDecels', 'variableDecels', 'tachycardia',
  'lateDecels', 'prolongedDecel', 'bradycardia', 'minimalVariability', 'sinusoidal',
];

// Variability band amplitude (± bpm) used by the engine + control labels.
export const VARIABILITY_BANDS = {
  absent:   1,
  minimal:  4,
  moderate: 11,
  marked:   28,
};

// The fetal-state fields a pattern carries (everything except display metadata).
export const FETAL_FIELD_KEYS = [
  'fhrBaseline', 'variability', 'accels', 'decel', 'sinusoidal',
  'contractionFreq', 'contractionStrength', 'uterineTone',
];

/**
 * Clinically-appropriate CTG for each OB/GYN scenario, surfaced as a toggle when
 * the scenario is active. A value is either a preset key (string) or
 * `{ preset, ...overrides }` to tweak a preset's fields for that scenario.
 * Postpartum scenarios (atony, inversion) are intentionally omitted — the fetus
 * is already delivered, so there is no intrapartum tracing.
 */
export const SCENARIO_FETAL = {
  // Antepartum haemorrhage / uteroplacental insufficiency → late decels
  obgynHemorrhagicShock: { preset: 'lateDecels', fhrBaseline: 160, variability: 'minimal' },
  obPlacentalAbruption:  { preset: 'lateDecels', fhrBaseline: 100, variability: 'minimal', uterineTone: 35, contractionFreq: 6, contractionStrength: 75 },
  obPlacentaAccreta:     'normal',   // usually reassuring antepartum until delivery/haemorrhage
  obHELLP:               'lateDecels',
  obgynSCAD:             'lateDecels',
  obgynPeripartumCM:     'lateDecels',
  obgynHighSpinal:       { preset: 'lateDecels', fhrBaseline: 110 },   // maternal hypotension
  obPulmonaryEdema:      'lateDecels',
  obAspiration:          'lateDecels',

  // Maternal hypoxia / collapse → prolonged deceleration / bradycardia
  amnioticFluidEmbolism: { preset: 'prolongedDecel', fhrBaseline: 90, variability: 'absent' },
  obgynEclampsia:        'prolongedDecel',   // fetal brady during the seizure
  obSeizure:             'prolongedDecel',
  obVAE:                 { preset: 'prolongedDecel', fhrBaseline: 95 },
  obFailedIntubation:    { preset: 'bradycardia', fhrBaseline: 95 },
  obgynCardiacArrest:    { preset: 'bradycardia', fhrBaseline: 70, variability: 'absent', accels: false },

  // Acute cord compression → prolonged deceleration / bradycardia
  obCordProlapse:        { preset: 'prolongedDecel', fhrBaseline: 80, variability: 'minimal' },

  // Chorioamnionitis / maternal fever → fetal tachycardia
  obMaternalSepsis:      'tachycardia',

  // Magnesium → reduced FHR variability
  obMagnesiumToxicity:   'minimalVariability',
};

/**
 * Resolve a scenario's CTG mapping to a fetal-state patch (or null). The returned
 * object is safe to spread into `setFetal(...)` and carries a `pattern` marker so
 * the toggle can tell whether it is currently applied.
 */
export function getScenarioFetal(scenarioKey) {
  const m = SCENARIO_FETAL[scenarioKey];
  if (!m) return null;
  const presetKey = typeof m === 'string' ? m : m.preset;
  const preset = FETAL_PATTERNS[presetKey];
  if (!preset) return null;
  const patch = { pattern: presetKey };
  for (const k of FETAL_FIELD_KEYS) if (preset[k] !== undefined) patch[k] = preset[k];
  if (typeof m === 'object') for (const k of FETAL_FIELD_KEYS) if (m[k] !== undefined) patch[k] = m[k];
  return patch;
}
